import { RefObject, useCallback, useEffect, useRef, useState } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import { useConditionalEffect, usePrevious } from 'react-delta';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';

import * as K from 'common/constants';
import { HELP_ROUTE } from 'components/help/constants';
import ELEMENT from 'common/element';
import { MenuItemConfig } from 'common/types';
import { getCursorPosition, setCursorPosition } from 'service/browser';
import { Match, TOKEN_TYPE } from 'common/util/filter/types';
import { COMPARATORS } from 'common/util/filter/constants';
import { getMatches } from 'common/util/filter/command';
import { numericStringCompare } from 'common/util/string';
import { setMark } from 'common/PerformanceLogger';

import DropdownItems from 'components/common/DropdownItems/DropdownItems';
import OverlayTrigger from 'components/common/OverlayTrigger/OverlayTrigger';
import { draftSearchParamState } from 'components/common/Managers/RouteStateManager.state';
import { facetInfoState } from 'components/search/facets.state';

import SearchHelpPanel from 'components/search/SearchHelper/SearchHelpPanel';

import {
  CommandInfo,
  FunctionInfo,
  HelpItem,
  PowerQueryAggregationFunctionInfo,
  PowerQueryGroupFunctionInfo,
  PowerQueryNetworkFunctionInfo,
  PowerQueryNumericFunctionInfo,
  PowerQueryStringFunctionInfo,
  PowerQueryTimeFunctionInfo,
} from './help';

import './SearchHelper.scss';

// fields needed to maintain state of the autocomplete widget
type MatchState = {
  matches: Match[]; // list of matches (does not include headers)
  matchedText: string; // text that matching was done against (replaced during completion)
  focusIndex: number; // index of currently focused match
};

enum SECTION_TYPE {
  KEYWORDS = 'keywords',
  COMPARISON = 'comparison',
  COMMANDS = 'commands',
  FUNCTIONS = 'functions',
  FIELDS = 'fields',
  VALUES = 'values',
  BOOLEAN = 'boolean',
}

type MatchSection = {
  type: SECTION_TYPE;
  tokenTypes: TOKEN_TYPE[];
  limit?: number;
};

type SearchHelperProps = {
  children: JSX.Element;
  showSearchHelper: boolean;
  setShowSearchHelper: (value: boolean) => void;
  inputRef: RefObject<HTMLTextAreaElement | HTMLInputElement | HTMLDivElement>;
  onChange: (value: string) => void;
  excludeTypes?: TOKEN_TYPE[];
  onEnter?: () => void;
  onCursorChange?: (cursorPos: number) => void;
  useShiftOnEnter?: boolean;
  useMetaOnEnter?: boolean;
};

// default limit how many matches we show for a single group
const DEFAULT_MAX_AUTOCOMPLETE_MATCHES_PER_SECTION = 10;

// this structure controls the layout of the suggestions as an ordered list of sections
const SECTIONS: MatchSection[] = [
  {
    type: SECTION_TYPE.KEYWORDS,
    tokenTypes: [TOKEN_TYPE.WHERE, TOKEN_TYPE.TIMESHIFT, TOKEN_TYPE.FROM, TOKEN_TYPE.BY, TOKEN_TYPE.ON],
  },
  {
    type: SECTION_TYPE.COMPARISON,
    tokenTypes: [TOKEN_TYPE.COMPARISON, TOKEN_TYPE.MODIFIER],
  },
  {
    type: SECTION_TYPE.COMMANDS,
    tokenTypes: [TOKEN_TYPE.COMMAND],
    limit: 20, // show all matching commands
  },
  {
    type: SECTION_TYPE.FUNCTIONS,
    tokenTypes: [
      TOKEN_TYPE.FUNCTION,
      TOKEN_TYPE.PQ_GROUP_FUNCTION,
      TOKEN_TYPE.PQ_AGGREGATION_FUNCTION,
      TOKEN_TYPE.PQ_NETWORK_FUNCTION,
      TOKEN_TYPE.PQ_NUMERIC_FUNCTION,
      TOKEN_TYPE.PQ_STRING_FUNCTION,
      TOKEN_TYPE.PQ_TIME_FUNCTION,
    ],
  },
  {
    type: SECTION_TYPE.FIELDS,
    tokenTypes: [TOKEN_TYPE.FIELD, TOKEN_TYPE.PQ_FIELD],
  },
  {
    type: SECTION_TYPE.VALUES,
    tokenTypes: [TOKEN_TYPE.VALUE],
  },
  {
    type: SECTION_TYPE.BOOLEAN,
    tokenTypes: [TOKEN_TYPE.BOOLEAN],
  },
];

export const HELP_SECTION: Record<TOKEN_TYPE, HelpItem[] | null> = {} as Record<TOKEN_TYPE, HelpItem[] | null>;
Object.values(TOKEN_TYPE).forEach((key) => {
  HELP_SECTION[key as TOKEN_TYPE] = null;
});
HELP_SECTION[TOKEN_TYPE.FUNCTION] = FunctionInfo;
HELP_SECTION[TOKEN_TYPE.COMMAND] = CommandInfo;
HELP_SECTION[TOKEN_TYPE.PQ_GROUP_FUNCTION] = PowerQueryGroupFunctionInfo;
HELP_SECTION[TOKEN_TYPE.PQ_NUMERIC_FUNCTION] = PowerQueryNumericFunctionInfo;
HELP_SECTION[TOKEN_TYPE.PQ_STRING_FUNCTION] = PowerQueryStringFunctionInfo;
HELP_SECTION[TOKEN_TYPE.PQ_AGGREGATION_FUNCTION] = PowerQueryAggregationFunctionInfo;
HELP_SECTION[TOKEN_TYPE.PQ_TIME_FUNCTION] = PowerQueryTimeFunctionInfo;
HELP_SECTION[TOKEN_TYPE.PQ_NETWORK_FUNCTION] = PowerQueryNetworkFunctionInfo;

const HELP_LINK: Record<TOKEN_TYPE, string> = {} as Record<TOKEN_TYPE, string>;
Object.values(TOKEN_TYPE).forEach((key) => {
  HELP_LINK[key as TOKEN_TYPE] = '';
});
HELP_LINK[TOKEN_TYPE.FUNCTION] = HELP_ROUTE.HELP_QUERY_LANGUAGE_GRAPH_FUNCTIONS;
HELP_LINK[TOKEN_TYPE.COMMAND] = HELP_ROUTE.HELP_POWER_QUERIES;
HELP_LINK[TOKEN_TYPE.PQ_GROUP_FUNCTION] = HELP_ROUTE.HELP_POWER_QUERIES_GROUP;
HELP_LINK[TOKEN_TYPE.PQ_NUMERIC_FUNCTION] = HELP_ROUTE.HELP_POWER_QUERIES_FUNCTIONS;
HELP_LINK[TOKEN_TYPE.PQ_STRING_FUNCTION] = HELP_ROUTE.HELP_POWER_QUERIES_FUNCTIONS;
HELP_LINK[TOKEN_TYPE.PQ_AGGREGATION_FUNCTION] = HELP_ROUTE.HELP_POWER_QUERIES_FUNCTIONS;
HELP_LINK[TOKEN_TYPE.PQ_TIME_FUNCTION] = HELP_ROUTE.HELP_POWER_QUERIES_FUNCTIONS;
HELP_LINK[TOKEN_TYPE.PQ_NETWORK_FUNCTION] = HELP_ROUTE.HELP_POWER_QUERIES_FUNCTIONS;

/**
 * This component is an enhanced autocomplete popup that is intended to guide a user through our query syntax by
 * parsing the filter text and recognizing what can come next. That may be a facet name, an operator, a power query
 * command, a function, etc.
 *
 * The text used for matching is taken from the global draft filter. Whether the list of matches is showing is
 * controlled by the owning component. The list only appears if there are matches. The SearchHelper includes an
 * OverlayTrigger that wraps the owning component's element.
 *
 * The bottom item in the list of matches is a Help item that will open up a panel to the right with more
 * information, based on the current context.
 *
 * Matching and insertion are done where the cursor is (which is normally at the end).
 *
 * @prop children               owning component's element (some sort of input)
 * @prop showSearchHelper       if true, search helper is visible (must have matches to show anything)
 * @prop setShowSearchHelper    function used to display/hide search helper
 * @prop inputRef               ref to the input (TEXTAREA or INPUT) with which this component is associated
 * @prop onChange               function to call when the filter has changed (after completion)
 * @prop excludeTypes           token types to exclude from matching
 * @prop onEnter                function to call if user types a return
 * @prop onCursorChange         function to call when the cursor position has changed
 * @prop useShiftOnEnter        if true, Shift plus Enter triggers onPressEnter action
 * @prop useMetaOnEnter         if true, Cmd or Win plus Enter triggers onPressEnter action
 *
 * TODO: add an item at the top of the autocomplete list showing valid types of filter text?
 */
function SearchHelper({
  children,
  showSearchHelper,
  setShowSearchHelper,
  inputRef,
  onChange,
  excludeTypes = [],
  onEnter,
  onCursorChange,
  useShiftOnEnter = false,
  useMetaOnEnter = false,
}: SearchHelperProps): JSX.Element {
  const [draftParams] = useRecoilState(draftSearchParamState);
  const filter = draftParams[K.URL_PARAM.FILTER] || '';

  // the help overlay appears to the right for functions and commands
  const [showHelpOverlay, setShowHelpOverlay] = useState(false);

  // hash of matches by section type
  const [matches, setMatches] = useState<Record<SECTION_TYPE, Match[]>>(
    K.EMPTY_OBJECT as Record<SECTION_TYPE, Match[]>,
  );

  // list of matches (does not include headers)
  const [matchList, setMatchList] = useState<Match[]>(K.EMPTY_ARRAY as Match[]);

  const [matchedText, setMatchedText] = useState('');
  const [focusIndex, setFocusIndex] = useState(-1);

  const matchStateRef = useRef({} as MatchState);

  const facetInfo = useRecoilValue(facetInfoState);

  const gotMatches = filter && matches && matchList.length > 0;

  // Replace the currently matched text with the completion value from the list of matches.
  const doCompletion = useCallback(
    (match: Match, matched: string): void => {
      const input = inputRef.current;
      let newFilter = filter;
      let newCursorPos = -1;
      const cursorPos = (inputRef.current && getCursorPosition(inputRef.current)) || -1;
      if (cursorPos === -1 || cursorPos === filter.length) {
        newFilter = filter.substring(0, filter.length - matched.length) + match.completionValue;
      } else {
        newFilter =
          filter.substring(0, cursorPos - matched.length) + match.completionValue + filter.substring(cursorPos);
        newCursorPos = cursorPos - matched.length + match.completionValue.length;
      }
      onChange(newFilter);
      if (input) {
        setTimeout(() => setCursorPosition(input, newCursorPos));
      }
      if (onCursorChange) {
        onCursorChange(newCursorPos);
      }
      setMark('searchHelperCompletion', { searchHelperCompletionType: match.type });
    },
    [filter, inputRef, onChange, onCursorChange],
  );

  // returns the help object for the given item
  const getHelp = (match: Match): HelpItem | undefined => {
    if (!match) {
      return undefined;
    }
    const helpSection = HELP_SECTION[match.type];
    const key = match.text.replace(/\(\)$/, ''); // strip closing () from functions
    return helpSection ? helpSection.find((item: HelpItem) => item.name === key) : undefined;
  };

  // Listen for possible autocomplete commands in the associated input.
  const onKeyDown = useCallback(
    (ev: Event): void => {
      let handled = true;
      const event = ev as KeyboardEvent;
      const matchState = matchStateRef.current;
      const match = matchState.matches?.[focusIndex];
      const numItems = matchState.matches?.length || 0;
      const isShowing = showSearchHelper && gotMatches;

      if (event.key === 'ArrowDown') {
        if (isShowing && numItems > 1) {
          setFocusIndex((index) => (index < numItems - 1 ? index + 1 : 0));
        } else {
          handled = false;
        }
      } else if (event.key === 'ArrowUp') {
        if (isShowing && numItems > 1) {
          setFocusIndex((index) => (index > 0 ? index - 1 : numItems - 1));
        } else {
          handled = false;
        }
      } else if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        if (isShowing && getHelp(match)) {
          setShowHelpOverlay(event.key === 'ArrowRight');
        } else {
          handled = false;
        }
      } else if (event.key === 'Tab') {
        if (isShowing) {
          doCompletion(match || matchState.matches?.[0], matchState.matchedText);
        }
      } else if (event.key === 'Enter') {
        if (match) {
          doCompletion(match, matchState.matchedText);
        } else {
          setShowSearchHelper(false);
          if (
            onEnter &&
            ((!useShiftOnEnter && !useMetaOnEnter) ||
              (useShiftOnEnter && event.shiftKey) ||
              (useMetaOnEnter && event.metaKey))
          ) {
            onEnter();
          } else {
            handled = false;
          }
        }
      } else {
        handled = false;
      }

      // since this is keydown, we need to wait for UI to settle before updating cursor position
      setTimeout(() => {
        if (matchStateRef.current && inputRef.current) {
          const cursorPos = getCursorPosition(inputRef.current) || -1;
          if (onCursorChange) {
            onCursorChange(cursorPos);
          }
        }
      });

      // eat the event if we handled it
      if (handled) {
        event.preventDefault();
      }
    },
    [doCompletion, focusIndex, onEnter, setShowSearchHelper, inputRef, onCursorChange, useShiftOnEnter, useMetaOnEnter],
  );

  // update cursor position when user clicks in input
  const onClick = useCallback((): void => {
    if (matchStateRef.current && inputRef.current) {
      const cursorPos = getCursorPosition(inputRef.current) || -1;
      if (onCursorChange) {
        onCursorChange(cursorPos);
      }
    }
  }, [inputRef, onCursorChange]);

  // add key and click handlers
  useEffect(() => {
    const input = inputRef.current;
    if (input) {
      input.addEventListener('keydown', onKeyDown, false);
      input.addEventListener('click', onClick, false);
      return (): void => {
        input.removeEventListener('keydown', onKeyDown);
        input.removeEventListener('click', onClick);
      };
    }
    return undefined;
  }, [inputRef, onKeyDown, onClick]);

  // perform matching against the current filter and update state
  const performMatching = (): void => {
    // match text up to cursor (which is usually but not always at the end)
    const cursorPos = inputRef.current ? getCursorPosition(inputRef.current) : -1;
    const filterToCursor = cursorPos === -1 || cursorPos === filter.length ? filter : filter.substring(0, cursorPos);
    const matchInfo = getMatches(filterToCursor, facetInfo, excludeTypes);

    // process matches into sections
    let matchesList = [] as Match[];
    const matchesBySection = {} as Record<SECTION_TYPE, Match[]>;
    SECTIONS.forEach((section) => {
      let sectionItems = section.tokenTypes.reduce(
        (acc, type) => (matchInfo.matches[type] ? acc.concat(matchInfo.matches[type]) : acc),
        [] as Match[],
      );
      if (section.type === SECTION_TYPE.COMPARISON) {
        sectionItems.sort((a: Match, b: Match) => COMPARATORS.indexOf(a.text) - COMPARATORS.indexOf(b.text));
      } else {
        sectionItems.sort((a: Match, b: Match) => numericStringCompare(a.text, b.text));
      }
      sectionItems = sectionItems.slice(0, section.limit || DEFAULT_MAX_AUTOCOMPLETE_MATCHES_PER_SECTION);

      if (sectionItems.length > 0) {
        matchesList = matchesList.concat(sectionItems);
        matchesBySection[section.type] = sectionItems;
      }
    });

    setMatches(matchesBySection);
    setMatchList(matchesList);
    setMatchedText(matchInfo.matchedText);
    setFocusIndex(-1);
    matchStateRef.current = {
      matches: matchesList,
      matchedText: matchInfo.matchedText,
      focusIndex: -1,
    };
  };

  // If the filter changes, get matches. If the focused item changes, update our match state to capture it.
  const prevFilter = usePrevious(filter);
  const prevFocusIndex = usePrevious(focusIndex);
  useConditionalEffect(() => {
    if (filter && filter !== prevFilter) {
      setTimeout(() => {
        setShowHelpOverlay(false); // hide help panel when filter changes
        performMatching();
      });
    }
  }, (filter && filter !== prevFilter) || focusIndex !== prevFocusIndex);

  // hide help panel if there is no filter
  useConditionalEffect(() => {
    setShowHelpOverlay(false);
  }, !filter && filter !== prevFilter);

  // map matches to sections with MenuItemConfig objects
  const sections: MenuItemConfig[][] = [];
  let itemIndex = -1;
  SECTIONS.forEach((sectionConfig) => {
    const sectionItems = matches[sectionConfig.type];
    const section = [];
    if (sectionItems?.length > 0) {
      section.push({
        text: sectionConfig.type.toUpperCase(),
        className: 'SearchHelper__ItemHeader',
      } as MenuItemConfig);
      sectionItems.forEach((match) => {
        itemIndex += 1;
        const isCurrentItem = itemIndex === focusIndex;
        const item: MenuItemConfig = {
          focused: isCurrentItem,
          clickHandler: (): void => doCompletion(match, matchedText),
        };
        const helpInfo = getHelp(match);
        const summary = helpInfo?.summary;
        if (isCurrentItem && summary) {
          item.children = (
            <>
              <span className="SearchHelper__ItemName">{match.text}</span>
              <span className="SearchHelper__ItemDescription">{summary}</span>
            </>
          );
          item.hoverActions = [
            {
              icon: (
                <span className="SearchHelper__ItemHelp">
                  <span>Help</span>
                  <ExitToAppIcon fontSize="small" />
                </span>
              ),
              action: (): void => setShowHelpOverlay(true),
              testId: ELEMENT.searchHelperHelp,
            },
          ];
        } else {
          item.text = match.text;
        }
        const enterHandler = setFocusIndex.bind(null, itemIndex);
        item.enterHandler = (): void => enterHandler();
        section.push(item);
      });
      sections.push(section);
    }
  });

  const list = <DropdownItems sections={sections} testId={ELEMENT.searchHelper} itemClass="SearchHelper__Item" />;

  // set up the content of the help panel
  const curMatch = matchList[focusIndex];
  const helpInfo = getHelp(curMatch);
  let helpPanel = <></>;
  if (showHelpOverlay && helpInfo) {
    let helpLink = HELP_LINK[curMatch.type as TOKEN_TYPE];
    if (curMatch.type === TOKEN_TYPE.COMMAND) {
      helpLink += helpInfo.name.indexOf('join') !== -1 ? 'join' : helpInfo.name;
    }
    helpPanel = <SearchHelpPanel item={helpInfo} helpLink={helpLink} close={(): void => setShowHelpOverlay(false)} />;
  }

  // our main component is an overlay which has its own overlay
  const searchHelper = !gotMatches ? (
    <></>
  ) : (
    <div className="SearchHelper">
      <OverlayTrigger
        placement="right-start"
        overlay={helpPanel}
        showOverlay={showHelpOverlay}
        setShowOverlay={setShowHelpOverlay}
        disabled
      >
        {list}
      </OverlayTrigger>
    </div>
  );

  // we need to clear state when the search helper is hidden so we don't try to match against the stale state
  const setShowOverlay = (value: boolean): void => {
    setShowSearchHelper(value);
    if (!value) {
      matchStateRef.current = {
        matches: [],
        matchedText: '',
        focusIndex: -1,
      } as MatchState;
    }
  };

  return (
    <OverlayTrigger
      placement="bottom-start"
      overlay={searchHelper}
      popoverClass="Dropdown__Menu"
      showOverlay={showSearchHelper}
      setShowOverlay={setShowOverlay}
      className="FilterInput__Wrapper"
      disabled
    >
      {children}
    </OverlayTrigger>
  );
}

export default SearchHelper;
